import { useMemo, Dispatch, SetStateAction } from 'react'
import { FormikProps } from 'formik'
import * as yup from 'yup'
import { useReselector } from '@/utils/sharedHooks'
import { faTrashAlt, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { selectAdminOrUserFinancialAccountOptions } from '@/selectors/financeSelectors'
import { selectTransactionCategoriesAsDropdownOptions } from '@/features/reports/reports.selectors'
import { Form, Text, Button, Alert, Label } from '@/ui'
import { InitialJournalEntryFormValues } from '../types'

// TODO: Migrate to v2
import { makeNumberSchema } from '@/componentsV1/BaseComponents'
import { TextColors, TypographyStyles } from '@/styles'

interface JournalEntryLineInputsProps {
  formik: FormikProps<InitialJournalEntryFormValues>
  userId?: string
  totals: {
    debitTotal: string
    creditTotal: string
  }
  totalError: boolean
  setJournalEntryLinesToDelete: Dispatch<SetStateAction<number[]>>
}

export const JournalEntryLineInputs = ({
  formik,
  userId,
  totals,
  totalError,
  setJournalEntryLinesToDelete,
}: JournalEntryLineInputsProps) => {
  const financialAccountOptions = useReselector(
    selectAdminOrUserFinancialAccountOptions,
    userId
  )

  const categoryOptions = useReselector(
    selectTransactionCategoriesAsDropdownOptions
  )

  const lineItemDropdownOptions = useMemo(
    () => [
      ...financialAccountOptions.map((option) => ({
        value: `${option.value} fin`,
        text: option.text,
      })),
      ...categoryOptions.map((option) => ({
        value: `${option.value} cat`,
        text: option.text,
      })),
    ],
    [financialAccountOptions, categoryOptions]
  )

  // return error if a journal entry line has both a debit and a credit
  const hasLineError = useMemo(() => {
    return formik.values.journalEntryLines.some((line) => {
      const debitAmount = parseFloat(line.debitAmount)
      const creditAmount = parseFloat(line.creditAmount)
      return debitAmount !== 0 && creditAmount !== 0
    })
  }, [formik.values])

  const addJournalEntryLine = () => {
    formik.setValues((prevValues) => {
      const newLine = {
        debitAmount: '0.00',
        creditAmount: '0.00',
        accountOrCategoryId: null,
      }

      return {
        ...prevValues,
        journalEntryLines: [...prevValues.journalEntryLines, newLine],
      }
    })
  }

  const removeJournalEntryLine = (indexToRemove: number) => {
    formik.setValues((prevValues) => {
      const updatedLines = [...prevValues.journalEntryLines]
      const lineToDelete = updatedLines[indexToRemove]
      const id = lineToDelete.id
      if (id) {
        setJournalEntryLinesToDelete((prevValue) => [...prevValue, id])
      }
      updatedLines.splice(indexToRemove, 1)
      return {
        ...prevValues,
        journalEntryLines: updatedLines,
      }
    })
  }

  return (
    <>
      {/* Header row */}
      <div className="grid grid-cols-12 gap-4 items-center mb-2">
        <div className="col-span-4">
          <Label>Account</Label>
        </div>
        <div className="col-span-4">
          <Label>Debit</Label>
        </div>
        <div className="col-span-4">
          <Label>Credit</Label>
        </div>
      </div>

      {/* Entry lines */}
      {formik.values.journalEntryLines.map((line, index) => (
        <div
          key={`${line.id} ${index}`}
          className="grid grid-cols-12 gap-4 items-center mb-2"
        >
          <div className="col-span-4">
            <Form.Select
              searchable
              name={`journalEntryLines.${index}.accountOrCategoryId`}
              options={lineItemDropdownOptions}
              className="w-full"
              schema={yup.string().required('Field is required.')}
            />
          </div>
          <div className="col-span-4">
            <Form.Input
              name={`journalEntryLines.${index}.debitAmount`}
              schema={makeNumberSchema({ allowedDecimals: 2 }).test(
                'Test failed',
                '',
                (debitAmount) => {
                  if (totals.debitTotal !== totals.creditTotal) return false
                  const debitNum = parseFloat(debitAmount || '0')
                  const creditNum = parseFloat(
                    formik.values.journalEntryLines[index].creditAmount
                  )
                  if (creditNum === 0 && debitNum === 0) return false
                  return !(debitNum !== 0 && creditNum !== 0)
                }
              )}
              componentType="currency"
              className="w-full"
            />
          </div>
          <div className="col-span-4">
            <Form.Input
              name={`journalEntryLines.${index}.creditAmount`}
              schema={makeNumberSchema({ allowedDecimals: 2 }).test(
                'Test failed',
                '',
                (creditAmount) => {
                  if (totals.debitTotal !== totals.creditTotal) return false
                  const creditNum = parseFloat(creditAmount || '0')
                  const debitNum = parseFloat(
                    formik.values.journalEntryLines[index].debitAmount
                  )
                  if (creditNum === 0 && debitNum === 0) return false
                  return !(debitNum !== 0 && creditNum !== 0)
                }
              )}
              componentType="currency"
              className="w-full"
            />
          </div>
          {index > 1 && (
            <div className="col-span-1 flex justify-end">
              <Button
                variant="discreet"
                onClick={() => removeJournalEntryLine(index)}
                iconLeft={faTrashAlt}
              />
            </div>
          )}
        </div>
      ))}

      {/* Totals Row */}
      {totals && (
        <div className="grid grid-cols-12 gap-4 items-center mb-2">
          <div className="col-span-4">
            <Button
              onClick={addJournalEntryLine}
              iconLeft={faPlus}
              className="mr-4 mb-2"
            >
              Add line
            </Button>
          </div>
          <div className="col-span-4">
            <Text
              className="ml-4 break-words"
              variant={TypographyStyles.BODY_SM}
              color={totalError ? TextColors.DANGER : TextColors.MUTED}
            >
              {totals.debitTotal}
            </Text>
          </div>
          <div className="col-span-4">
            <Text
              className="ml-4 break-words"
              variant={TypographyStyles.BODY_SM}
              color={totalError ? TextColors.DANGER : TextColors.MUTED}
            >
              {totals.creditTotal}
            </Text>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {hasLineError && (
        <div className="mt-2">
          <Alert
            variant="error"
            description="An amount can only be entered in one field per row."
          />
        </div>
      )}
    </>
  )
}
